import {
  EmployerInfoData,
  EmployerInfoDataList,
} from 'hooks/webapi/foxentry/FoxentryApiTypes';
import React, { useState, useCallback } from 'react';
import classes from 'classnames';
import { Input } from 'components';
import useDebounce from 'hooks/useDebounce'; // Assuming the hook is in the same folder
import { v4 as uuid } from 'uuid';
import styles from './Autocomplete.module.scss';

type AutocompleteProps = {
  fetchFunction?: (
    query: string,
  ) => Promise<{ response: { results: EmployerInfoDataList } }>; // Optional API fetch function
  options?: EmployerInfoDataList; // Optional static list of options
  placeholder?: string; // Placeholder text for the input field
  onOptionSelect?: (option: EmployerInfoData | undefined) => void; // Callback when an option is selected
  renderOption: (option: EmployerInfoData) => React.ReactNode; // Function to render an option
  getOptionLabel: (option: EmployerInfoData) => string; // Function to get the label for an option
  debounceTime?: number; // Debounce time for the search input
  dropdownItemClassName?: string;
  className?: string;
  dataTest?: string;
  withSearchIcon?: boolean;
  defaultValue?: string;
  disabled?: boolean;
};

export default function Autocomplete({
  fetchFunction,
  options = [],
  placeholder = 'Search...',
  onOptionSelect,
  renderOption,
  getOptionLabel,
  debounceTime = 500,
  withSearchIcon,
  dropdownItemClassName,
  className,
  dataTest = 'autocomplete',
  defaultValue,
  disabled = false,
}: AutocompleteProps) {
  const defaultQueryValue = defaultValue || '';

  const [query, setQuery] = useState<string>(defaultQueryValue); // Search input value
  const [results, setResults] = useState<EmployerInfoDataList>([]); // Results to display in dropdown
  const [isLoadingQuery, setIsLoadingQuery] =
    useState<string>(defaultQueryValue); // Loading related to specific query
  const [isManualUpdate, setIsManualUpdate] = useState<boolean>(false); // Flag to prevent fetch on programmatic query updates

  const debouncedSearch = useCallback(() => {
    if (isLoadingQuery === query) return; // same query already loaded

    if (isManualUpdate) {
      setIsManualUpdate(false); // Reset the flag
      return; // Prevent fetch on programmatic updates
    }

    if (query) {
      if (fetchFunction) {
        onOptionSelect?.(undefined); // clear selected option
        fetchFunction(query)
          .then(({ response: { results: resultsFetched } }) => {
            setResults(resultsFetched);
          })
          .catch(() => {
            setResults([]);
          })
          .finally(() => setIsLoadingQuery(query)); // Hide loading animation
      } else {
        const filteredOptions = options.filter((option) => {
          const queryString = query.toLowerCase();
          getOptionLabel(option).toLowerCase().includes(queryString);

          setResults(filteredOptions);
          setIsLoadingQuery(query); // Hide loading animation
        });
      }
    } else {
      setResults([]); // Clear results if input is empty
      setIsLoadingQuery(query); // Hide loading animation
    }
  }, [query, fetchFunction, options, getOptionLabel]);

  useDebounce(debouncedSearch, debounceTime, [query]); // Debounce the search with a 300ms delay

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsManualUpdate(false); // This is a user-triggered update
    const { value } = e.target;
    setQuery(value);
  };

  // Handle selection of an option
  const handleSelect = (option: EmployerInfoData) => {
    setIsManualUpdate(true); // Set flag to prevent fetch
    const querySelected = getOptionLabel(option);
    setQuery(querySelected); // Set input value to selected option's label
    setIsLoadingQuery(querySelected); // loading not needed after this query update
    if (onOptionSelect) {
      onOptionSelect(option); // Notify parent about the selected option
    }
    setResults([]); // Clear dropdown after selection
  };

  return (
    <div className={classes([styles.wrapper, className])} data-test={dataTest}>
      <Input
        value={query}
        type="text"
        withSearchIcon={withSearchIcon}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={styles.input}
        isLoading={isLoadingQuery !== query}
        dataTest={`${dataTest}-input`}
        disabled={disabled}
      />

      {/* Dropdown for displaying results */}
      {results.length > 0 && isLoadingQuery === query && (
        <ul className={styles.dropdown} data-test={`${dataTest}-list`}>
          {results.map((option, index) => (
            <li
              key={uuid()}
              onClick={() => handleSelect(option)}
              className={classes(
                styles['dropdown-item'],
                dropdownItemClassName,
              )}
              data-test={`${dataTest}-li-${index}`}
            >
              {renderOption(option)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
